<template>
  <div class="w-100">
    <div class="list-box-upload" :class="{ 'error': validateError }">
      <div class="scroll-wrapper mesh-upload-wrapper">
        <p class="sub-title" v-if="subTitle" >{{ subTitle }}</p>
    
        <div v-if="meshFiles.length > 0 && selectedUids.length > 0" class="d-flex justify-content-between align-items-center mb-2">
          
          <!-- Checkbox to select all items  -->
          <div class="d-flex align-items-center gap-1">
            <el-checkbox 
              :model-value="selectedUids.length === meshFiles.length"
              @change="(val: boolean) => onSelectAllFile(val)" 
            />
            <div class="">{{ $t(`components.mesh-loader.selectAll`) }}</div>
          </div>

          <!-- Button to delete item selected  -->
          <el-button 
            type="danger" 
            size="small" 
            @click="handleMultipleDeleteFile"
          >
            {{ deleteButtonText }}
          </el-button>
        </div>
    
        <el-upload
          v-model:file-list="fileList"
          multiple
          action="#"
          drag
          list-type="picture-card"
          :before-upload="beforeUpload"
          :http-request="httpRequest"
          accept=".stl,.obj,.fbx,.mtl"
          class="custom-upload-item"
        >
          <template #default>
            <div>
              <font-awesome-icon icon="upload" class="primary" size="lg" />
              <div class="placeholder-text">
                {{ $t('components.mesh-loader.upload') }}
              </div>
            </div>
          </template>
      
          <template #file="{ file }">
            <div
              @mouseenter="isHoverUid = file.uid"
              @mouseleave="isHoverUid = null"
            >
              <el-checkbox
                v-if="selectedUids.find(uid => uid === file.uid) || file.uid === isHoverUid"
                class="select-checkbox"
                :model-value="selectedUids.includes(file.uid)"
                @update:model-value="(val: boolean) => onSelectFile(file, val)"
              />
              <PreviewRenderer
                :meshes="[
                  {
                    filetype: getFileType(file.name),
                    filename: file.name,
                    url: file.url,
                    raw: file.raw,
                  },
                ]"
                size="76"
                :can-remove="true"
                :can-select="false"
                @remove-item="() => handleRemove(file, templateUploadImageType.object3D)"
              />
            </div>
          </template>
        </el-upload>
      </div>
      <div class="sub-title">
        {{ messageCommand }}
      </div>
    </div>
    <div class="sub-title text-danger my-1" v-if="validateError">
      {{ $t('error.vuelidate.required') }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import PreviewRenderer from '@/components/three/scenes/PreviewRenderer.vue'
import { FileData } from '../../../types/enum/file'
import { ElMessageBox } from 'element-plus'
import i18n from '@/i18n';

const props = defineProps({
  selected: {
    type: Array,
    default: () => [],
  },
  meshFiles: {
    type: Array,
    default: () => []
  },
  beforeUpload: {
    type: Function,
    required: true
  },
  uploadFile: {
    type: Function,
    required: true
  },
  getFileType: {
    type: Function,
    required: true
  },
  handleRemove: {
    type: Function,
    required: true
  },
  templateUploadImageType: {
    type: Object,
    required: true
  },
  subTitle: { 
    type: String,
    default: '' 
  },
  desription: { 
    type: String,
    default: '' 
  },
  validateError: {
    type: Boolean,
    default: () => false,
  }
})

const emits = defineEmits(['update:meshFiles', 'update:selectedUids'])
const selectedUids = ref<number[]>([])
const isHoverUid = ref<number | null>(null)

const fileList = computed<FileData[]>({
  get: () => props.meshFiles as FileData[],
  set: (val) => emits('update:meshFiles', val)
})

const messageCommand = computed(() =>
  props.meshFiles?.length === 0 && selectedUids.value.length === 0
    ? props.desription
    : i18n.global.t(`components.mesh-loader.deleteCommandMessage`)
);

const deleteButtonText = computed(() =>
  `${i18n.global.t(`components.mesh-loader.delete`)} (${selectedUids.value.length})`
);

const httpRequest = (uploadArgs: any) => {
  props.uploadFile(uploadArgs, props.templateUploadImageType.object3D)
}

function onSelectAllFile(checked: boolean) {
  selectedUids.value = checked
    ? fileList.value.map(item => item.uid)
    : []
}

function onSelectFile(file: FileData, checked: boolean) {
  if (!checked) {
    selectedUids.value = selectedUids.value.filter(uid => uid !== file.uid)
  }
  else {
    const check = selectedUids.value.find(uid => uid === file.uid);
    if (check) return;
    selectedUids.value = [...selectedUids.value, file.uid];
  }

}

// Function to handle multple delete items 
async function handleMultipleDeleteFile() {
  await ElMessageBox.confirm(
    i18n.global.t('confirm.delete.message'),
    i18n.global.t('confirm.delete.title'),
    {
      confirmButtonText: i18n.global.t('confirm.delete.ok'),
      cancelButtonText: i18n.global.t('confirm.delete.cancel'),
      type: 'warning',
    }
  );
  emits('update:selectedUids', selectedUids.value)
  selectedUids.value = [];
}
</script>

<style scoped>
.custom-upload-item {
  width: 100%;
}

:deep(.el-upload--picture-card) {
  width: 76px !important;
  height: 76px !important;
}

:deep(.el-upload-list--picture-card .el-upload-list__item) {
  width: 76px !important;
  height: 76px !important;
  margin: 0 !important;
  display: flex;
}

:deep(.el-upload-list--picture-card) {
  display: flex !important;
  flex-wrap: wrap !important;
  gap: 8px !important;
}

.placeholder-text {
  font-size: 12px;
  /* line-height: 16px; */
}

.sub-title {
  font-size: 12px;
  line-height: 16px;
  color: rgba(0, 0, 0, 0.67);
}

.mesh-upload-wrapper {
  position: relative;
  width: 100%;
}

.select-checkbox {
  position: absolute;
  top: -4px !important;
  left: 4px;
  z-index: 10;
}
.btn-icon {
  width: 30px;
  height: 30px;
  border-radius: 6px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #ffffff;
}

::v-deep(.el-upload-list--picture-card) {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}
</style>
